import React from "react";
import { Navigate, Outlet, Route, useLocation } from "react-router";
import CallbackPage from "src/ui/containers/account/CallbackPage/CallbackPage";
import ConfirmRegistrationPage from "src/ui/containers/account/ConfirmRegistrationPage/ConfirmRegistrationPage";
import LoginPage from "src/ui/containers/account/LoginPage/LoginPage";
import LogoutPage from "src/ui/containers/account/LogoutPage";
import PasswordRecoveryPage from "src/ui/containers/account/PasswordRecoveryPage/PasswordRecoveryPage";
import PasswordResetPage from "src/ui/containers/account/PasswordResetPage";
import RegisterPage from "src/ui/containers/account/RegisterPage/RegisterPage";
import SettingsPage from "src/ui/containers/account/SettingsPage/SettingsPage";
import AdminAccountsPage from "src/ui/containers/admin/components/AdminAccountsPage";
import AdminAnnouncementsPage from "src/ui/containers/admin/components/AdminAnnouncementsPage/AdminAnnouncementsPage";
import AdminBuildsPage from "src/ui/containers/admin/components/AdminBuildsPage/AdminBuildsPage";
import AdminCertificatePage from "src/ui/containers/admin/components/AdminCertificatePage";
import AdminEmailPage from "src/ui/containers/admin/components/AdminEmailPage/AdminEmailPage";
import AdminEventsPage from "src/ui/containers/admin/components/AdminEventsPage/AdminEventsPage";
import AdminExpirationsPage from "src/ui/containers/admin/components/AdminExpirationsPage";
import AdminFdroidPage from "src/ui/containers/admin/components/AdminFdroidPage/AdminFdroidPage";
import AdminFilesPage from "src/ui/containers/admin/components/AdminFilesPage/AdminFilesPage";
import AdminGroupsPage from "src/ui/containers/admin/components/AdminGroupsPage/AdminGroupsPage";
import AdminMembersPage from "src/ui/containers/admin/components/AdminMembersPage";
import AdminClientPage from "src/ui/containers/admin/components/AdminOauthClientPage/AdminOauthClientPage";
import AdminProductsPage from "src/ui/containers/admin/components/AdminProductsPage/AdminProductsPage";
import AdminRolesPage from "src/ui/containers/admin/components/AdminRolesPage/AdminRolesPage";
import AdminWorkspacesPage from "src/ui/containers/admin/components/AdminWorkspacesPage/AdminWorkspacesPage";
import ClientsOverviewPage from "src/ui/containers/clients/overview/ClientsOverviewPage";
import UniversalLicensesPage from "src/ui/containers/dashboard/licenses/UniversalLicensesPage";
import ReleaseNotesDevicePage from "src/ui/containers/embedded/release-notes/ReleaseNotesDevicePage";
import ReleaseNotesPage from "src/ui/containers/embedded/release-notes/ReleaseNotesPage";
import OssLicensesPage from "src/ui/containers/legal/OssLicensesPage";

import DevicesPage from "@dashboard/devices/DevicesPage";
import DownloadsPage from "@dashboard/downloads/DownloadsPage";
import EnrollmentPage from "@dashboard/enrollment/EnrollmentPage";
import UploadPage from "@dashboard/files/UploadPage";
import GroupsPage from "@dashboard/groups/GroupsPage";
import InvitationPage from "@dashboard/invitation/InvitationPage";
import OverviewPage from "@dashboard/overview/OverviewPage";
import ProductPage from "@dashboard/products/ProductPage";
import ProvisioningPage from "@dashboard/provisioning/ProvisioningPage";
import RepositoriesPage from "@dashboard/repositories/RepositoriesPage";
import RolloutsPage from "@dashboard/rollouts/RolloutsPage";
import WorkspaceConfigurationPage from "@dashboard/workspaces/WorkspaceConfigurationPage";
import WorkspaceMembersPage from "@dashboard/workspaces/WorkspaceMembersPage";
import WorkspacesIntroPage from "@dashboard/workspaces/WorkspacesIntroPage";
import WorkspaceSubscriptionPage from "@dashboard/workspaces/WorkspaceSubscriptionPage";
import { ApmRoutes } from "@elastic/apm-rum-react";
import { ScreenViewPage } from "@screenView/ui/ScreenViewPage/ScreenViewPage";

import { PageForbidden, PageNotFound } from "./components/NavigationPages";
import { AuthGuard } from "./guards/AuthGuard";
import { PageGuard } from "./guards/PageGuard";
import { PrefetchGuard } from "./guards/PrefetchGuard";

export default function Router() {
    const location = useLocation();

    React.useEffect(() => {
        // Skip scroll for embedded pages (iframe context)
        if (location.pathname.startsWith("/embedded/")) {
            return;
        }

        if (window.self === window.top) {
            window.scrollTo(0, 0);
        }
    }, [location]);

    return (
        <ApmRoutes>
            <Route path="/authorization/callback" element={<CallbackPage />} />
            <Route element={<PageGuard />}>
                <Route path="/invitation" element={<InvitationPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LogoutPage />} />
                <Route path="/register" element={<RegisterPage />} />

                <Route path="/screenview" element={<ScreenViewPage />} />

                <Route path="account">
                    <Route path="confirm" element={<ConfirmRegistrationPage />} />
                    <Route path="recover" element={<PasswordRecoveryPage />} />
                    <Route path="reset" element={<PasswordResetPage />} />
                </Route>

                <Route element={<AuthGuard />}>
                    <Route element={<PrefetchGuard />}>
                        <Route path="account">
                            <Route
                                path="2fa/disable"
                                element={<Navigate to="/account/settings?tab=security&mode=2fa-disable" replace />}
                            />
                            <Route
                                path="2fa/enable"
                                element={<Navigate to="/account/settings?tab=security&mode=2fa-enable" replace />}
                            />
                            <Route path="delete" element={<Navigate to="/account/settings?tab=profile&mode=delete" replace />} />
                            <Route path="settings" element={<SettingsPage />} />
                            <Route path="workspaces">
                                <Route index element={<WorkspacesIntroPage />} />
                                <Route path="members" element={<WorkspaceMembersPage />} />
                                <Route path="subscription" element={<WorkspaceSubscriptionPage />} />
                                <Route path="configuration" element={<WorkspaceConfigurationPage />} />
                            </Route>
                        </Route>
                    </Route>

                    <Route path="admin">
                        <Route path="accounts" element={<AdminAccountsPage />} />
                        <Route path="builds" element={<AdminBuildsPage />} />
                        <Route path="certificate" element={<AdminCertificatePage />} />
                        <Route path="clients" element={<AdminClientPage />} />
                        <Route path="email" element={<AdminEmailPage />} />
                        <Route path="events" element={<AdminEventsPage />} />
                        <Route path="expirations" element={<AdminExpirationsPage />} />
                        <Route path="fdroid-repos" element={<AdminFdroidPage />} />
                        <Route path="files" element={<AdminFilesPage />} />
                        <Route path="groups" element={<AdminGroupsPage />} />
                        <Route path="members" element={<AdminMembersPage />} />
                        <Route path="announcements" element={<AdminAnnouncementsPage />} />
                        <Route path="products" element={<AdminProductsPage />} />
                        <Route path="roles" element={<AdminRolesPage />} />
                        <Route path="workspaces" element={<AdminWorkspacesPage />} />
                    </Route>

                    <Route element={<PrefetchGuard />}>
                        <Route index element={<Navigate to="/dashboard/overview" replace />} />
                        <Route path="dashboard">
                            <Route index element={<Navigate to="/dashboard/overview" replace />} />
                            <Route path="devices" element={<DevicesPage />} />
                            <Route path="enrollment" element={<EnrollmentPage />} />
                            <Route path="downloads" element={<DownloadsPage />} />
                            <Route path="files" element={<UploadPage />} />
                            <Route path="groups" element={<GroupsPage />} />
                            <Route path="licenses" element={<UniversalLicensesPage />} />
                            <Route path="overview" element={<OverviewPage />} />
                            <Route path="products" element={<ProductPage />} />
                            <Route path="products/:codename/:androidVersion" element={<ProductPage />} />
                            <Route path="provisioning" element={<ProvisioningPage />} />
                            <Route path="provisioning/:provisioningId" element={<ProvisioningPage />} />
                            <Route path="repositories" element={<RepositoriesPage />} />
                            <Route path="rollouts" element={<RolloutsPage />} />
                            <Route path="workspaces" element={<Navigate to="/account/workspaces" replace />} />
                        </Route>

                        <Route path="clients">
                            <Route path="overview" element={<ClientsOverviewPage />} />
                        </Route>
                    </Route>
                </Route>

                <Route path="embedded">
                    <Route path="release-notes" element={<ReleaseNotesPage />} />
                    <Route path="release-notes/:device" element={<ReleaseNotesDevicePage />} />
                    <Route path="third-party" element={<OssLicensesPage />} />
                </Route>

                <Route path="/forbidden" element={<PageForbidden />} />
                <Route path="*" element={<PageNotFound />} />
            </Route>
        </ApmRoutes>
    );
}
